import { useCallback, useMemo } from 'react';
import { orderBy } from 'lodash-es';
import { ResourceWarning } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/hooks/useResourceWarnings/types';
import {
  createMissingShiftWarningHandler,
  createPrimaryWarningHandler,
} from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/hooks/useResourceWarnings/handlers';
import { getOMSColumns } from './config';
import { OMSRowData, renderOMSTableCell } from './renderOMSTableCell';
import { useOMSForAssignment } from '@/queries/resource/resource.query';
import { Case, CaseUserGroup } from '@/api/apis/case';
import { searchCandidates } from '../ClinicalSpecialistsTable/utils';
import { CandidatesTable } from '../components/CandidatesTable/CandidatesTable';
import { EmployeeRole } from '@/api/apis/resources/resources.types';
import { OMSShiftType } from '@/api/apis/resources/schedule/schedule.types';

export type OMSTableProps = {
  onAssign: ({ id }: { id: string }) => void;
  toggleWarning: (warning: ResourceWarning) => void;
  idsToAssign?: string[];
  searchTerm: string;
  caseInfo: Case;
};

export const OMSTable = ({ onAssign, toggleWarning, idsToAssign = [], searchTerm, caseInfo }: OMSTableProps) => {
  const { assignedEmployees } = caseInfo;
  const omsList = assignedEmployees[CaseUserGroup.OrganManagementSpecialists] || [];
  const assignedEmployeeIds = omsList.map((e) => e.employeeId).filter(Boolean) as string[];
  const primaryOMSId = omsList.find((e) => e.isPrimary)?.employeeId;

  const { data, isLoading } = useOMSForAssignment(assignedEmployeeIds);

  const items = useMemo(() => {
    if (!data) return [];
    const mappedOMS = data.map((c) => ({
      ...c,
      isLast: idsToAssign.length === 1,
      isPrimary: c.employeeId === primaryOMSId,
      isSelected: idsToAssign.includes(c.employeeId),
    }));
    return orderBy(mappedOMS, 'isPrimary', 'desc');
  }, [data, primaryOMSId, idsToAssign]);

  const searchResults = useMemo(() => searchCandidates(searchTerm, items), [searchTerm, items]);

  const onOMSAssign = useCallback(
    ({ employeeId, employeeFullName, isPrimary, isAssigned, currentSlotShiftType }: OMSRowData) => {
      const updatePrimaryWarning = createPrimaryWarningHandler(toggleWarning);
      const updateMissingShiftWarning = createMissingShiftWarningHandler(toggleWarning);
      const hasOnCallShift = currentSlotShiftType === OMSShiftType.OnCall;

      if (isPrimary) updatePrimaryWarning(employeeId, employeeFullName, EmployeeRole.OrganManagementSpecialist);
      if (!isAssigned && !hasOnCallShift)
        updateMissingShiftWarning(employeeId, employeeFullName, EmployeeRole.OrganManagementSpecialist);

      onAssign({ id: employeeId });
    },
    [onAssign, toggleWarning],
  );

  const renderCell = useMemo(() => renderOMSTableCell(onOMSAssign), [onOMSAssign]);

  return (
    <CandidatesTable
      label="Organ Management Specialist for assignment"
      columns={getOMSColumns()}
      renderCell={renderCell}
      data={searchResults}
      isLoading={isLoading}
      isFetching={isLoading}
    />
  );
};
